import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApi, publicApi } from "../hooks/useApi";
import { useUser } from "../hooks/useAuthCompat";
import { usePlayer } from "../context/PlayerContext";
import { AskAboutThis } from "../components/AskAboutThis";
import { toast } from "sonner";
import { Play, Heart } from "lucide-react";

export default function EpisodeDetail() {
  const { id } = useParams();
  const api = useApi();
  const { isSignedIn } = useUser();
  const { play } = usePlayer();
  const [favorited, setFavorited] = useState(false);
  const { data: ep, isLoading } = useQuery({ queryKey: ["episode", id], queryFn: async () => (await publicApi.get(`/episodes/${id}`)).data });

  const toggleFav = async () => {
    if (!isSignedIn) return toast.error("Sign in to save favorites.");
    try {
      const res = await api.post(`/episodes/${id}/favorite`);
      setFavorited(res.data.favorited);
      toast.success(res.data.favorited ? "Added to favorites" : "Removed from favorites");
    } catch {
      toast.error("Could not update favorite.");
    }
  };

  const playEpisode = () => {
    play({ id: ep.id, title: ep.title, subtitle: "Episode", image: ep.image_url, audioUrl: ep.audio_url });
    if (isSignedIn) api.post(`/episodes/${ep.id}/history?position_seconds=0`).catch(() => {});
  };

  if (isLoading || !ep) return <div className="max-w-3xl mx-auto px-6 py-24 text-white/70">Loading episode…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-16" data-testid="episode-detail-page">
      <div className="flex flex-col md:flex-row gap-8">
        <img src={ep.image_url} alt="" className="w-full md:w-64 h-64 object-cover shrink-0" />
        <div>
          <p className="text-xs uppercase tracking-widest text-textSecondary mb-2">Episode {ep.episode_number || "—"} · {ep.review_status.replace(/_/g, " ")}</p>
          <h1 className="font-display text-display-sm md:text-display-md mb-5">{ep.title}</h1>
          <div className="flex gap-3">
            <button
              data-testid="episode-detail-play-button"
              onClick={playEpisode}
              className="bg-white text-black px-5 py-3 text-sm uppercase tracking-wide flex items-center gap-2 hover:bg-white/80 transition-colors duration-200"
            >
              <Play className="w-4 h-4" /> Play Episode
            </button>
            <button data-testid="episode-detail-favorite-button" onClick={toggleFav} className="border border-white/20 px-4 py-3 hover:border-brandAccent transition-colors duration-200">
              <Heart className={`w-4 h-4 ${favorited ? "fill-brandAccent text-brandAccent" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="prose prose-invert max-w-none mt-10 text-white/70" dangerouslySetInnerHTML={{ __html: ep.description }} />

      <div className="mt-10">
        <AskAboutThis type="episode" refId={ep.id} title={ep.title} />
      </div>
    </div>
  );
}
